import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User as UserIcon,
  Lock,
  Bell,
  Globe,
  Link2,
  Shield,
  FolderKanban,
  Trash2,
  Loader2,
  AlertTriangle,
  ExternalLink,
  CheckCircle,
  RefreshCw,
  Users,
  BarChart3,
  X,
  Check,
  Pencil,
  Palette,
  Gift,
  Code,
  Building2,
  Megaphone,
  Target,
  ShoppingCart,
  TrendingUp,
  Eye,
  MousePointerClick,
  LayoutGrid,
  ArrowUpRight,
  Cloud,
  Sparkles
} from 'lucide-react';

// Interface for connected social account
interface ConnectedAccount {
  id: string;
  platform: string;
  username?: string;
  account_id?: string;
  profile_image?: string;
  followers?: number;
  following?: number;
  posts_count?: number;
  connected: boolean;
  last_sync?: string;
  metadata?: any;
}

// Interface for available page/account to select
interface AvailablePage {
  id: string;
  name: string;
  username?: string;
  picture?: string;
  followers?: number;
  subscribers?: number;
  type: string;
  category?: string;
  facebook_page_id?: string;
  facebook_page_name?: string;
  urn?: string;
  // Ad account fields
  currency?: string;
  business_name?: string;
  account_status?: number;
}
import type { User, NotificationSettings } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { SOCIAL_PLATFORMS, ADS_PLATFORMS, PlatformDefinition } from '../../constants';
import { authService } from '../../services/authService';
import socialService from '../../services/socialService';
import openaiAdsService from '../../services/openaiAdsService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import LanguageSelector from '../../components/layout/LanguageSelector';
import VercelConnect from '../../components/integrations/VercelConnect';
import projectService from '../../services/projectService';
import WhitelabelSettings from './WhitelabelSettings';
import EmployeesSettings from './EmployeesSettings';
import ModulesSettings from './ModulesSettings';
import AffiliateSettings from './AffiliateSettings';
import APISettings from './APISettings';
import AiUsageSettings from './AiUsageSettings';

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const { user, updateUser, refreshUser } = useAuth();
  const { projects, currentProject, deleteProject: deleteProjectFn, refreshProjects } = useProject();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [editProjectData, setEditProjectData] = useState({ name: '', description: '', color: '' });
  const [editProfileData, setEditProfileData] = useState<any>({
    business_name: '',
    business_description: '',
    website_url: '',
    industry: '',
    brand_voice: 'professional',
    target_audience: { description: '' },
    marketing_goals: [] as string[],
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [connectingIntegration, setConnectingIntegration] = useState<string | null>(null);
  const [refreshingAccount, setRefreshingAccount] = useState<string | null>(null);

  // Page selection modal state
  const [showPageSelectionModal, setShowPageSelectionModal] = useState(false);
  const [pageSelectionAccountId, setPageSelectionAccountId] = useState<string | null>(null);
  const [pageSelectionPlatform, setPageSelectionPlatform] = useState<string | null>(null);
  const [availablePages, setAvailablePages] = useState<AvailablePage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const [selectingPage, setSelectingPage] = useState(false);

  // FB Page selection modal state (for facebook_ads)
  const [showFbPageSelectionModal, setShowFbPageSelectionModal] = useState(false);

  // ChatGPT Ads (OpenAI) — conexão por API key
  const [showOpenaiKeyModal, setShowOpenaiKeyModal] = useState(false);
  const [openaiKeyInput, setOpenaiKeyInput] = useState('');
  const [openaiKeySaving, setOpenaiKeySaving] = useState(false);
  const [fbPageSelectionAccountId, setFbPageSelectionAccountId] = useState<string | null>(null);
  const [availableFbPages, setAvailableFbPages] = useState<AvailablePage[]>([]);
  const [selectedFbPageId, setSelectedFbPageId] = useState<string | null>(null);
  const [loadingFbPages, setLoadingFbPages] = useState(false);
  const [selectingFbPage, setSelectingFbPage] = useState(false);

  // Vercel configuration - when user comes from Vercel dashboard
  const vercelConfigurationId = searchParams.get('configurationId');
  const sourceFromVercel = searchParams.get('source') === 'vercel';

  // Set active tab from URL params or when coming from Vercel
  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab);
    } else if (vercelConfigurationId || sourceFromVercel) {
      setActiveTab('integrations');
    }
  }, [urlTab, vercelConfigurationId, sourceFromVercel]);

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    company_name: user?.company_name || user?.company || '',
    phone: user?.phone || ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Initialize notification settings from user data or defaults
  const defaultNotificationSettings: NotificationSettings = {
    email_reports: true,
    email_alerts: true,
    email_tips: false,
    push_notifications: true
  };

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(
    user?.notification_settings || defaultNotificationSettings
  );

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        company_name: user.company_name || user.company || '',
        phone: user.phone || ''
      });
      // Update notification settings if available
      if (user.notification_settings) {
        setNotificationSettings(user.notification_settings);
      }
    }
  }, [user]);

  // Load connected accounts for current project (including ads platforms)
  const loadConnectedAccounts = async () => {
    try {
      const response = await socialService.getAccountsWithAds(currentProject?.id);
      if (response.success && response.data) {
        setConnectedAccounts(response.data);
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  useEffect(() => {
    loadConnectedAccounts();
  }, [currentProject?.id]);

  // Check for OAuth success/error in URL params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const provider = searchParams.get('provider');
    const selectPage = searchParams.get('select_page');
    const selectFbPage = searchParams.get('select_fb_page');
    const accountId = searchParams.get('account_id');

    if (success === 'true' && provider) {
      // Check if we need to show page selection modal
      if (selectPage === 'true' && accountId) {
        setPageSelectionAccountId(accountId);
        setPageSelectionPlatform(provider);
        loadAvailablePages(accountId);
        setShowPageSelectionModal(true);
        // Clean only some params, keep modal open
        searchParams.delete('success');
        searchParams.delete('select_page');
        searchParams.delete('account_id');
        setSearchParams(searchParams);
      } else if (selectFbPage === 'true' && accountId) {
        // Show FB page selection modal for facebook_ads
        setFbPageSelectionAccountId(accountId);
        loadAvailableFbPages(accountId);
        setShowFbPageSelectionModal(true);
        // Clean only some params, keep modal open
        searchParams.delete('success');
        searchParams.delete('select_fb_page');
        searchParams.delete('account_id');
        setSearchParams(searchParams);
      } else {
        toast.success(t('social.accountConnected'));
        loadConnectedAccounts(); // Reload accounts
        // Clean URL params
        searchParams.delete('success');
        searchParams.delete('provider');
        setSearchParams(searchParams);
      }
    } else if (error) {
      // Show error toast with longer duration so user has time to read
      const providerLabel = provider ? ` (${provider})` : '';
      toast.error(`${t('social.oauthError')}${providerLabel}: ${error}`, { duration: 10000 });
      console.error(`[OAuth] Connection error for ${provider}:`, error);
      searchParams.delete('error');
      searchParams.delete('provider');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  // Load available pages for selection
  const loadAvailablePages = async (accountId: string) => {
    setLoadingPages(true);
    try {
      const response = await socialService.getAvailablePages(accountId);
      if (response.success && response.data) {
        setAvailablePages(response.data.pages || []);
        setSelectedPageId(response.data.selected_page_id || null);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error(t('settings.pageSelection.errorLoadingPages'));
    } finally {
      setLoadingPages(false);
    }
  };

  // Handle page selection
  const handleSelectPage = async () => {
    if (!pageSelectionAccountId || !selectedPageId) return;

    const currentPlatform = pageSelectionPlatform;
    const currentAccountId = pageSelectionAccountId;

    setSelectingPage(true);
    try {
      const response = await socialService.selectPage(pageSelectionAccountId, selectedPageId);
      if (response.success) {
        toast.success(t('settings.pageSelection.pageSelected'));
        setShowPageSelectionModal(false);
        setPageSelectionAccountId(null);
        setPageSelectionPlatform(null);
        setAvailablePages([]);
        setSelectedPageId(null);
        // Reload accounts to reflect the selection
        await loadConnectedAccounts();
        // Clean remaining URL params
        searchParams.delete('provider');
        setSearchParams(searchParams);

        // For facebook_ads: after selecting ad account, chain FB Page selection
        if (currentPlatform === 'facebook_ads') {
          // Fetch pages data to check if FB pages are available
          try {
            const pagesResponse = await socialService.getAvailablePages(currentAccountId);
            const fbPages = pagesResponse.data?.fb_pages || [];
            if (fbPages.length > 1) {
              // Multiple FB pages - show selection modal
              setFbPageSelectionAccountId(currentAccountId);
              setAvailableFbPages(fbPages);
              setSelectedFbPageId(pagesResponse.data?.selected_fb_page_id || null);
              setShowFbPageSelectionModal(true);
            } else if (fbPages.length === 1) {
              // Single FB page - auto-select it
              await socialService.selectFbPage(currentAccountId, fbPages[0].id);
              toast.success(t('settings.pageSelection.fbPageSelected'));
              await loadConnectedAccounts();
            }
          } catch (e) {
            console.error('Error checking FB pages after ad account selection:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error selecting page:', error);
      toast.error(t('settings.pageSelection.errorSelectingPage'));
    } finally {
      setSelectingPage(false);
    }
  };

  // Close page selection modal
  const handleClosePageSelection = () => {
    setShowPageSelectionModal(false);
    setPageSelectionAccountId(null);
    setPageSelectionPlatform(null);
    setAvailablePages([]);
    setSelectedPageId(null);
    // Clean URL params
    searchParams.delete('provider');
    setSearchParams(searchParams);
    // Reload accounts anyway - the connection was made
    loadConnectedAccounts();
  };

  // Load available FB pages for facebook_ads
  const loadAvailableFbPages = async (accountId: string) => {
    setLoadingFbPages(true);
    try {
      const response = await socialService.getAvailablePages(accountId);
      if (response.success && response.data) {
        setAvailableFbPages(response.data.fb_pages || []);
        setSelectedFbPageId(response.data.selected_fb_page_id || null);
      }
    } catch (error) {
      console.error('Error loading FB pages:', error);
      toast.error(t('settings.pageSelection.errorLoadingPages'));
    } finally {
      setLoadingFbPages(false);
    }
  };

  // Handle FB page selection
  const handleSelectFbPage = async () => {
    if (!fbPageSelectionAccountId || !selectedFbPageId) return;

    setSelectingFbPage(true);
    try {
      const response = await socialService.selectFbPage(fbPageSelectionAccountId, selectedFbPageId);
      if (response.success) {
        toast.success(t('settings.pageSelection.fbPageSelected'));
        setShowFbPageSelectionModal(false);
        setFbPageSelectionAccountId(null);
        setAvailableFbPages([]);
        setSelectedFbPageId(null);
        await loadConnectedAccounts();
        searchParams.delete('provider');
        setSearchParams(searchParams);
      }
    } catch (error) {
      console.error('Error selecting FB page:', error);
      toast.error(t('settings.pageSelection.errorSelectingPage'));
    } finally {
      setSelectingFbPage(false);
    }
  };

  // Close FB page selection modal
  const handleCloseFbPageSelection = () => {
    setShowFbPageSelectionModal(false);
    setFbPageSelectionAccountId(null);
    setAvailableFbPages([]);
    setSelectedFbPageId(null);
    searchParams.delete('provider');
    setSearchParams(searchParams);
    loadConnectedAccounts();
  };

  // Helper to check if platform is connected
  const isPlatformConnected = (platform: string) => {
    return connectedAccounts.some(acc => acc.platform === platform && acc.connected);
  };

  // Get account for platform
  const getAccountForPlatform = (platform: string) => {
    return connectedAccounts.find(acc => acc.platform === platform && acc.connected);
  };

  const handleConnectIntegration = async (platform: string) => {
    // ChatGPT Ads não usa OAuth: o cliente cola a API key do Ads Manager da OpenAI
    if (platform === 'openai_ads') {
      if (!currentProject?.id) {
        toast.error(t('settings.openaiAds.noProject', 'Selecione um projeto antes de conectar o ChatGPT Ads'));
        return;
      }
      setOpenaiKeyInput('');
      setShowOpenaiKeyModal(true);
      return;
    }
    setConnectingIntegration(platform);
    try {
      // Pass current project ID so integrations are per-project
      const response = await socialService.getOAuthUrl(platform, currentProject?.id);
      if (response.success && response.authUrl) {
        window.location.href = response.authUrl;
      } else {
        toast.error(response.message || t('errors.oauthNotConfigured', 'OAuth not configured for this platform'));
      }
    } catch (error: any) {
      console.error('OAuth error:', error);
      toast.error(error.response?.data?.message || t('errors.generic', 'Error connecting'));
    } finally {
      setConnectingIntegration(null);
    }
  };

  // Vindo da tela de Campanhas com ?connect=openai_ads: abre o modal direto
  // (antes o usuário era largado na aba e tinha que achar o card sozinho)
  useEffect(() => {
    if (searchParams.get('connect') === 'openai_ads' && currentProject?.id) {
      setOpenaiKeyInput('');
      setShowOpenaiKeyModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('connect');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentProject?.id]);

  const handleCloseOpenaiKeyModal = () => {
    setShowOpenaiKeyModal(false);
    setOpenaiKeyInput(''); // não deixa a key parada no state/DevTools
  };

  const handleSaveOpenaiKey = async () => {
    const key = openaiKeyInput.trim();
    if (!key) return;
    setOpenaiKeySaving(true);
    try {
      const response = await openaiAdsService.connect(key);
      if (response.success) {
        toast.success(t('settings.openaiAds.connected', 'ChatGPT Ads conectado com sucesso!'));
        handleCloseOpenaiKeyModal();
        await loadConnectedAccounts();
      } else {
        toast.error(response.message || t('settings.openaiAds.invalidKey', 'API key inválida'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settings.openaiAds.invalidKey', 'API key inválida'));
    } finally {
      setOpenaiKeySaving(false);
    }
  };

  const handleDisconnectIntegration = async (platform: string) => {
    if (!confirm(t('settings.confirmDisconnect'))) return;
    try {
      await socialService.disconnectOAuth(platform, currentProject?.id);
      toast.success(t('settings.disconnected'));
      // Reload accounts from server to ensure sync
      await loadConnectedAccounts();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error(t('errors.generic', 'Error disconnecting'));
    }
  };

  const handleRefreshAccount = async (platform: string) => {
    setRefreshingAccount(platform);
    try {
      // Re-fetch account data from provider
      await socialService.refreshAccount(platform);
      await loadConnectedAccounts();
      toast.success(t('settings.accountUpdated', 'Account updated!'));
    } catch (error) {
      console.error('Error refreshing account:', error);
      toast.error(t('settings.accountUpdateError', 'Error updating account'));
    } finally {
      setRefreshingAccount(null);
    }
  };

  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Check if user is an employee (not the account owner)
  const isEmployee = !!user?.ownerUserId;
  const employeeRole = user?.employeeRole;

  const allTabs = [
    { id: 'profile', label: t('settings.profile'), icon: UserIcon },
    { id: 'projects', label: t('settings.projects', 'Projects'), icon: FolderKanban },
    { id: 'employees', label: t('settings.employees.title', 'Team'), icon: Users, ownerOnly: true },
    { id: 'security', label: t('settings.security'), icon: Lock },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    { id: 'language', label: t('settings.language'), icon: Globe },
    { id: 'integrations', label: t('settings.integrations'), icon: Link2 },
    { id: 'platforms', label: t('nav.platforms', 'Platforms'), icon: LayoutGrid },
    { id: 'modules', label: t('modules.title', 'Módulos'), icon: LayoutGrid, ownerOnly: true },
    { id: 'ai-usage', label: t('settings.aiUsage.title', 'Uso de IA'), icon: Sparkles },
    { id: 'api', label: t('settings.api.title', 'API'), icon: Code, ownerOnly: true },
    { id: 'whitelabel', label: t('settings.whitelabel.title', 'Whitelabel'), icon: Palette, ownerOnly: true },
    { id: 'affiliate', label: t('settings.affiliate.title', 'Affiliate'), icon: Gift, ownerOnly: true }
  ];

  // Filter tabs based on user role - employees only see non-owner tabs
  const tabs = isEmployee && employeeRole !== 'admin'
    ? allTabs.filter(tab => !tab.ownerOnly)
    : allTabs;

  // Aba ownerOnly acessada por URL direta (ex.: /settings/api) por quem não tem
  // permissão cai no perfil em vez de renderizar a seção restrita.
  useEffect(() => {
    if (activeTab && !tabs.some(tab => tab.id === activeTab)) {
      setActiveTab('profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isEmployee, employeeRole]);

  const handleDeleteProject = async (projectId: string) => {
    setDeletingProjectId(projectId);
    try {
      await deleteProjectFn(projectId);
      await refreshProjects();
      toast.success(t('settings.projectDeleted', 'Project deleted successfully'));
      setShowDeleteConfirm(null);
    } catch (error: any) {
      toast.error(error.message || t('settings.projectDeleteError', 'Error deleting project'));
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleEditProject = async (project: any) => {
    setEditingProject(project);
    setEditProjectData({
      name: project.name || '',
      description: project.description || '',
      color: project.color || '#6366f1'
    });
    // Fetch full project with businessProfile
    setLoadingProfile(true);
    try {
      const response = await projectService.getProject(project.id);
      const bp = response.data?.businessProfile;
      if (bp) {
        setEditProfileData({
          business_name: bp.business_name || '',
          business_description: bp.business_description || '',
          website_url: bp.website_url || '',
          industry: bp.industry || '',
          brand_voice: bp.brand_voice || 'professional',
          target_audience: bp.target_audience || { description: '' },
          marketing_goals: bp.marketing_goals || [],
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleSaveProject = async () => {
    if (!editingProject) return;
    if (!editProjectData.name.trim()) {
      toast.error(t('settings.projectNameRequired', 'Project name is required'));
      return;
    }
    setSavingProject(true);
    try {
      await Promise.all([
        projectService.updateProject(editingProject.id, {
          name: editProjectData.name.trim(),
          description: editProjectData.description.trim() || undefined,
          color: editProjectData.color
        }),
        projectService.updateProjectProfile(editingProject.id, editProfileData)
      ]);
      await refreshProjects();
      toast.success(t('settings.projectUpdated', 'Project updated successfully'));
      setEditingProject(null);
    } catch (error: any) {
      toast.error(error.message || t('settings.projectUpdateError', 'Error updating project'));
    } finally {
      setSavingProject(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Send correct field names to the backend
      const dataToSend = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        company_name: profileData.company_name,
        phone: profileData.phone
      };
      const response = await authService.updateProfile(dataToSend);
      if (response.success && response.data) {
        updateUser(response.data);
        toast.success(t('messages.updatedSuccessfully'));
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error(t('errors.passwordTooShort'));
      return;
    }
    setIsLoading(true);
    try {
      await authService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success(t('messages.updatedSuccessfully'));
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setIsLoading(true);
    try {
      await authService.updateNotificationSettings(notificationSettings);
      // Refresh user to get updated notification settings
      await refreshUser();
      toast.success(t('messages.updatedSuccessfully'));
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.profileInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={t('auth.firstName')}
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  />
                  <Input
                    label={t('auth.lastName')}
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  />
                  <Input
                    label={t('auth.email')}
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    disabled
                  />
                  <Input
                    label={t('auth.company')}
                    value={profileData.company_name}
                    onChange={(e) => setProfileData({ ...profileData, company_name: e.target.value })}
                  />
                  <Input
                    label={t('settings.phone') + ' *'}
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+55 (11) 99999-9999"
                    required
                    minLength={8}
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" isLoading={isLoading}>
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        );

      case 'projects':
        return (
          <div className="space-y-3 sm:space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('settings.yourProjects', 'Your Projects')}</CardTitle>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('settings.projectsHint', 'To create a new project, use the project selector in the sidebar.')}
                </p>
              </CardHeader>
              <CardContent>
                {projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('settings.noProjectsFound', 'No projects found')}</p>
                    <Button
                      variant="primary"
                      className="mt-4"
                      onClick={() => navigate('/onboarding')}
                    >
                      {t('settings.createFirstProject', 'Create First Project')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                            style={{ backgroundColor: project.color || '#6366f1' }}
                          >
                            {project.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {project.name}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {project.description || t('common.noDescription', 'No description')}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                project.is_active
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                              }`}>
                                {project.is_active ? t('common.active', 'Active') : t('settings.archived', 'Archived')}
                              </span>
                              {project.businessProfile?.onboarding_completed ? (
                                <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.onboardingComplete', 'Onboarding complete')}</span>
                              ) : (
                                <span className="text-xs text-orange-500">{t('settings.onboardingPending', 'Onboarding pending')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {showDeleteConfirm === project.id ? (
                            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="text-sm text-red-700 dark:text-red-400">{t('common.confirm', 'Confirm')}?</span>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDeleteProject(project.id)}
                                isLoading={deletingProjectId === project.id}
                              >
                                {t('common.delete', 'Delete')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowDeleteConfirm(null)}
                              >
                                {t('common.cancel', 'Cancel')}
                              </Button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditProject(project)}
                                className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                                title={t('settings.editProject', 'Edit project')}
                              >
                                <Pencil className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm(project.id)}
                                className={`p-2 rounded-lg transition-colors ${
                                  projects.length <= 1
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                }`}
                                title={projects.length <= 1
                                  ? t('settings.cannotDeleteOnlyProject', 'Cannot delete the only project')
                                  : t('settings.deleteProject', 'Delete project')}
                                disabled={projects.length <= 1}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-3 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.changePassword')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <Input
                    label={t('settings.currentPassword')}
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  />
                  <Input
                    label={t('settings.newPassword')}
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  />
                  <Input
                    label={t('settings.confirmPassword')}
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  />
                  <div className="flex justify-end">
                    <Button type="submit" isLoading={isLoading}>
                      {t('settings.updatePassword')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

          </div>
        );

      case 'notifications':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.notificationPreferences')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { key: 'email_reports', label: t('settings.emailReports'), description: t('settings.emailReportsDesc') },
                  { key: 'email_alerts', label: t('settings.emailAlerts'), description: t('settings.emailAlertsDesc') },
                  { key: 'email_tips', label: t('settings.emailTips'), description: t('settings.emailTipsDesc') },
                  { key: 'push_notifications', label: t('settings.pushNotifications'), description: t('settings.pushNotificationsDesc') }
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings[item.key as keyof typeof notificationSettings]}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          [item.key]: e.target.checked
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                ))}
                <div className="flex justify-end pt-4">
                  <Button onClick={handleUpdateNotifications} isLoading={isLoading}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'language':
        return (
          <Card className="!overflow-visible">
            <CardHeader>
              <CardTitle>{t('settings.languageRegion')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.selectLanguage')}
                  </label>
                  <div className="flex items-center gap-4">
                    <LanguageSelector align="left" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {i18n.language === 'pt' ? 'Português (Brasil)' : 'English'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.timezone')}
                  </label>
                  <Select
                    options={[
                      { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (GMT-3)' },
                      { value: 'America/New_York', label: 'America/New_York (GMT-5)' },
                      { value: 'Europe/London', label: 'Europe/London (GMT+0)' },
                      { value: 'Europe/Paris', label: 'Europe/Paris (GMT+1)' }
                    ]}
                    value="America/Sao_Paulo"
                    onChange={() => {}}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.dateFormat')}
                  </label>
                  <Select
                    options={[
                      { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                      { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                      { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
                    ]}
                    value="DD/MM/YYYY"
                    onChange={() => {}}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'employees':
        return <EmployeesSettings />;

      case 'modules':
        return <ModulesSettings />;

      case 'whitelabel':
        return <WhitelabelSettings />;

      case 'affiliate':
        return <AffiliateSettings />;

      case 'ai-usage':
        return <AiUsageSettings />;

      case 'platforms':
        return (
          <div className="space-y-3 sm:space-y-6">
            {/* Header */}
            <Card>
              <CardContent>
                <div className="flex items-start gap-4 py-2">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-primary-500/10 to-purple-500/10 dark:from-primary-500/20 dark:to-purple-500/20">
                    <LayoutGrid className="w-7 h-7 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {t('platforms.sectionTitle', 'DevSkin Ecosystem')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                      {t('platforms.sectionDescription', 'Access all DevSkin platforms from one place.')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Platform Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  key: 'marketplace',
                  href: 'https://admin-marketplace.devskin.com',
                  color: '#16a34a',
                  gradient: 'from-green-500 to-emerald-600',
                  bgGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                  borderColor: 'border-green-200 dark:border-green-800/50',
                  icon: <ShoppingCart className="w-6 h-6" />,
                },
                {
                  key: 'marketing',
                  href: '#',
                  color: '#f97316',
                  gradient: 'from-orange-500 to-amber-500',
                  bgGradient: 'from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20',
                  borderColor: 'border-orange-200 dark:border-orange-800/50',
                  icon: <Megaphone className="w-6 h-6" />,
                  isCurrent: true,
                },
                {
                  key: 'crm',
                  href: 'https://admin-crm.devskin.com',
                  color: '#ef4444',
                  gradient: 'from-red-500 to-rose-600',
                  bgGradient: 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20',
                  borderColor: 'border-red-200 dark:border-red-800/50',
                  icon: <Users className="w-6 h-6" />,
                },
                {
                  key: 'analytics',
                  href: 'https://admin-flux.devskin.com',
                  color: '#3b82f6',
                  gradient: 'from-blue-500 to-indigo-600',
                  bgGradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
                  borderColor: 'border-blue-200 dark:border-blue-800/50',
                  icon: <BarChart3 className="w-6 h-6" />,
                },
                {
                  key: 'digitalSign',
                  href: 'https://admin-sign.devskin.com',
                  color: '#8b5cf6',
                  gradient: 'from-violet-500 to-purple-600',
                  bgGradient: 'from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
                  borderColor: 'border-violet-200 dark:border-violet-800/50',
                  icon: <Eye className="w-6 h-6" />,
                },
                {
                  key: 'cloud',
                  href: 'https://admin-cloud.devskin.com',
                  color: '#0ea5e9',
                  gradient: 'from-sky-500 to-sky-600',
                  bgGradient: 'from-sky-50 to-sky-50 dark:from-sky-900/20 dark:to-sky-900/20',
                  borderColor: 'border-sky-200 dark:border-sky-800/50',
                  icon: <Cloud className="w-6 h-6" />,
                },
              ].map((platform) => (
                <div
                  key={platform.key}
                  className={`relative overflow-hidden rounded-xl sm:rounded-2xl border ${platform.borderColor} bg-gradient-to-br ${platform.bgGradient} transition-all duration-300 flex flex-col ${
                    platform.isCurrent ? 'border-orange-300 dark:border-orange-600/50 shadow-sm' : 'hover:shadow-lg hover:-translate-y-0.5'
                  }`}
                >
                  <div className="p-4 sm:p-5 flex-1">
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${platform.gradient} text-white shadow-lg shrink-0`}>
                        {platform.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">
                          {t(`platforms.${platform.key}.name`, platform.key)}
                        </h3>
                        {platform.isCurrent && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold text-orange-700 dark:text-orange-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                            {t('platforms.currentPlatform', 'You are here')}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3 sm:mb-4">
                      {t(`platforms.${platform.key}.description`)}
                    </p>
                    {!platform.isCurrent && (
                      <a
                        href={platform.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors shadow-sm w-full sm:w-auto justify-center sm:justify-start"
                      >
                        {t('platforms.openPlatform', 'Open Platform')}
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  <div className={`h-1 bg-gradient-to-r ${platform.gradient}`} />
                </div>
              ))}
            </div>

            {/* Ecosystem Info */}
            <Card>
              <CardContent>
                <div className="flex items-start gap-4 py-2">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30">
                    <Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed pt-1">
                    {t('platforms.integratedEcosystem', 'All platforms share data seamlessly.')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'api':
        return <APISettings />;

      case 'integrations':
        return (
          <div className="space-y-3 sm:space-y-6">
            {/* Vercel Configuration Banner - when coming from Vercel */}
            {(vercelConfigurationId || sourceFromVercel) && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 76 65" className="w-5 h-5 fill-white">
                      <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200">
                      {t('integrations.vercel.configuringFromVercel', 'Configuring Vercel integration')}
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t('integrations.vercel.configuringFromVercelDesc', 'You came from the Vercel dashboard. Manage your integration below.')}
                    </p>
                    {vercelConfigurationId && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-mono">
                        Configuration ID: {vercelConfigurationId}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchParams({});
                    }}
                  >
                    {t('common.dismiss', 'Dismiss')}
                  </Button>
                </div>
              </div>
            )}

            {/* Vercel Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                    <svg viewBox="0 0 76 65" className="w-4 h-4 fill-white">
                      <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
                    </svg>
                  </div>
                  Vercel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <VercelConnect configurationId={vercelConfigurationId || undefined} />
              </CardContent>
            </Card>

            {/* Advertising Platforms */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {t('settings.adsPlatforms', 'Advertising Platforms')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ADS_PLATFORMS.map((platform: PlatformDefinition) => {
                    const integration = { name: platform.name, platform: platform.id, icon: platform.icon, color: platform.color, descriptionKey: platform.descriptionKey };
                    const account = getAccountForPlatform(integration.platform);
                    const isConnected = !!account;
                    const isConnecting = connectingIntegration === integration.platform;
                    const isRefreshing = refreshingAccount === integration.platform;
                    const selectedFbPage = account?.metadata?.selected_fb_page;
                    const hasFbPages = account?.metadata?.available_fb_pages?.length > 0;
                    const isFacebookAds = integration.platform === 'facebook_ads';

                    return (
                      <div key={integration.platform} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center text-white`}>
                              {integration.icon}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{integration.name}</p>
                              {isConnected && account?.username ? (
                                <p className="text-sm text-green-600 dark:text-green-400">
                                  {t('settings.connectedAs', 'Connected as')} {account.username}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {t(integration.descriptionKey)}
                                </p>
                              )}
                              {/* Show selected Facebook Page for facebook_ads */}
                              {isConnected && isFacebookAds && selectedFbPage && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                  {t('settings.pageSelection.fbPage')}: {selectedFbPage.name}
                                </p>
                              )}
                              {isConnected && isFacebookAds && !selectedFbPage && hasFbPages && (
                                <p className="text-xs text-orange-500 dark:text-orange-400 mt-0.5">
                                  {t('settings.pageSelection.noFbPageSelected')}
                                </p>
                              )}
                              {isConnected && isFacebookAds && !selectedFbPage && !hasFbPages && (
                                <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                                  {t('settings.pageSelection.noFbPagesFound')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isConnected ? (
                              <>
                                {/* Show change page button if account has multiple pages */}
                                {account?.metadata?.available_pages?.length > 1 && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setPageSelectionAccountId(account.id);
                                      setPageSelectionPlatform(integration.platform);
                                      loadAvailablePages(account.id);
                                      setShowPageSelectionModal(true);
                                    }}
                                    title={t('settings.pageSelection.change')}
                                  >
                                    {t('settings.pageSelection.change')}
                                  </Button>
                                )}
                                {/* Show select/change FB page button for facebook_ads */}
                                {isFacebookAds && hasFbPages && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setFbPageSelectionAccountId(account.id);
                                      loadAvailableFbPages(account.id);
                                      setShowFbPageSelectionModal(true);
                                    }}
                                  >
                                    {selectedFbPage
                                      ? t('settings.pageSelection.changeFbPage')
                                      : t('settings.pageSelection.selectFbPage')}
                                  </Button>
                                )}
                                {integration.platform !== 'openai_ads' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRefreshAccount(integration.platform)}
                                    disabled={isRefreshing}
                                  >
                                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                  </Button>
                                )}
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDisconnectIntegration(integration.platform)}
                                >
                                  {t('settings.disconnect')}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleConnectIntegration(integration.platform)}
                                disabled={isConnecting}
                                leftIcon={isConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : integration.platform === 'openai_ads' ? <Link2 className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                              >
                                {isConnecting ? t('common.loading') : t('settings.connect')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Social Media Accounts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t('settings.socialNetworks', 'Social Networks')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {SOCIAL_PLATFORMS.map((platform: PlatformDefinition) => {
                    const integration = { name: platform.name, platform: platform.id, icon: platform.icon, color: platform.color, descriptionKey: platform.descriptionKey };
                    const account = getAccountForPlatform(integration.platform);
                    const isConnected = !!account;
                    const isConnecting = connectingIntegration === integration.platform;
                    const isRefreshing = refreshingAccount === integration.platform;

                    return (
                      <div key={integration.platform} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {account?.profile_image ? (
                              <img
                                src={account.profile_image}
                                alt={account.username || integration.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center text-white`}>
                                {integration.icon}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{integration.name}</p>
                              {isConnected ? (
                                <div>
                                  <p className="text-sm text-green-600 dark:text-green-400">
                                    @{account?.username || t('settings.connected', 'Connected')}
                                  </p>
                                  {/* LinkedIn social API doesn't provide metrics, but LinkedIn Ads does */}
                                  {integration.platform !== 'linkedin' && (account?.followers !== undefined || account?.posts_count !== undefined) && (
                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      {account?.followers !== undefined && account.followers > 0 && (
                                        <span>{formatNumber(account.followers)} {t('settings.followers', 'followers')}</span>
                                      )}
                                      {account?.posts_count !== undefined && account.posts_count > 0 && (
                                        <span>{formatNumber(account.posts_count)} posts</span>
                                      )}
                                    </div>
                                  )}
                                  {/* Instagram missing permissions warning */}
                                  {integration.platform === 'instagram' && Array.isArray(account?.metadata?.granted_scopes) && account.metadata.granted_scopes.length > 0 && (() => {
                                    const scopes = account.metadata.granted_scopes as string[];
                                    const missing: string[] = [];
                                    if (!scopes.includes('instagram_business_manage_comments')) missing.push(t('settings.permissions.manageComments', 'Gerenciar comentários'));
                                    if (!scopes.includes('instagram_business_manage_messages')) missing.push(t('settings.permissions.manageMessages', 'Gerenciar mensagens'));
                                    if (!scopes.includes('instagram_business_content_publish')) missing.push(t('settings.permissions.contentPublish', 'Publicar conteúdo'));
                                    if (missing.length === 0) return null;
                                    return (
                                      <div className="flex items-start gap-1.5 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                        <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                                        <div className="text-xs text-yellow-700 dark:text-yellow-300">
                                          <p className="font-medium">{t('settings.permissions.missing', 'Permissões não concedidas:')}</p>
                                          <p>{missing.join(', ')}</p>
                                          <p className="mt-1 text-yellow-600 dark:text-yellow-400">
                                            {t('settings.permissions.reconnectHint', 'Reconecte a conta e aceite todas as permissões para que as automações funcionem.')}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {t(integration.descriptionKey)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isConnected ? (
                              <>
                                {/* Show change page button if account has multiple pages */}
                                {account?.metadata?.available_pages?.length > 1 && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setPageSelectionAccountId(account.id);
                                      setPageSelectionPlatform(integration.platform);
                                      loadAvailablePages(account.id);
                                      setShowPageSelectionModal(true);
                                    }}
                                    title={t('settings.pageSelection.change')}
                                  >
                                    {t('settings.pageSelection.change')}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRefreshAccount(integration.platform)}
                                  disabled={isRefreshing}
                                  title={t('common.refresh')}
                                >
                                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDisconnectIntegration(integration.platform)}
                                >
                                  {t('settings.disconnect')}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleConnectIntegration(integration.platform)}
                                disabled={isConnecting}
                                leftIcon={isConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : integration.platform === 'openai_ads' ? <Link2 className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                              >
                                {isConnecting ? t('common.loading') : t('settings.connect')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('settings.manageAccount')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-6">
        {/* Sidebar */}
        <Card className="lg:col-span-1 h-fit">
          <CardContent>
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="lg:col-span-3">{renderContent()}</div>
      </div>

      {/* Edit Project Modal */}
      {editingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 flex flex-col" style={{ maxHeight: '85vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('settings.editProject', 'Edit Project')}
              </h3>
              <button
                onClick={() => setEditingProject(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6 overflow-y-auto flex-1">
              {loadingProfile ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : (
                <>
                  {/* Section 1: Project */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FolderKanban className="w-4 h-4 text-primary-500" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        {t('settings.sectionProject', 'Project')}
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.projectName', 'Project Name')} *
                        </label>
                        <Input
                          value={editProjectData.name}
                          onChange={(e) => setEditProjectData({ ...editProjectData, name: e.target.value })}
                          placeholder={t('settings.projectNamePlaceholder', 'Enter project name')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.projectDescription', 'Description')}
                        </label>
                        <textarea
                          value={editProjectData.description}
                          onChange={(e) => setEditProjectData({ ...editProjectData, description: e.target.value })}
                          placeholder={t('settings.projectDescriptionPlaceholder', 'Enter project description')}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.projectColor', 'Color')}
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={editProjectData.color}
                            onChange={(e) => setEditProjectData({ ...editProjectData, color: e.target.value })}
                            className="w-12 h-10 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                          />
                          <div className="flex gap-2">
                            {['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'].map((color) => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => setEditProjectData({ ...editProjectData, color })}
                                className={`w-8 h-8 rounded-lg transition-transform ${
                                  editProjectData.color === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Section 2: Business */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-4 h-4 text-primary-500" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        {t('settings.sectionBusiness', 'Business')}
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.businessName', 'Business Name')}
                        </label>
                        <Input
                          value={editProfileData.business_name}
                          onChange={(e) => setEditProfileData({ ...editProfileData, business_name: e.target.value })}
                          placeholder={t('settings.businessNamePlaceholder', 'Your business name')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.businessDescription', 'Business Description')}
                        </label>
                        <textarea
                          value={editProfileData.business_description}
                          onChange={(e) => setEditProfileData({ ...editProfileData, business_description: e.target.value })}
                          placeholder={t('settings.businessDescriptionPlaceholder', 'Describe what your business does...')}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.website', 'Website')}
                        </label>
                        <Input
                          value={editProfileData.website_url}
                          onChange={(e) => setEditProfileData({ ...editProfileData, website_url: e.target.value })}
                          placeholder="https://example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.industry', 'Industry')}
                        </label>
                        <select
                          value={editProfileData.industry}
                          onChange={(e) => setEditProfileData({ ...editProfileData, industry: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">{t('settings.selectIndustry', 'Select an industry')}</option>
                          {[
                            { value: 'technology', label: t('onboarding.briefing.industries.technology', 'Technology') },
                            { value: 'ecommerce', label: t('onboarding.briefing.industries.ecommerce', 'E-commerce') },
                            { value: 'health', label: t('onboarding.briefing.industries.health', 'Health') },
                            { value: 'education', label: t('onboarding.briefing.industries.education', 'Education') },
                            { value: 'finance', label: t('onboarding.briefing.industries.finance', 'Finance') },
                            { value: 'food', label: t('onboarding.briefing.industries.food', 'Food') },
                            { value: 'fashion', label: t('onboarding.briefing.industries.fashion', 'Fashion') },
                            { value: 'beauty', label: t('onboarding.briefing.industries.beauty', 'Beauty') },
                            { value: 'realestate', label: t('onboarding.briefing.industries.realestate', 'Real Estate') },
                            { value: 'automotive', label: t('onboarding.briefing.industries.automotive', 'Automotive') },
                            { value: 'travel', label: t('onboarding.briefing.industries.travel', 'Travel') },
                            { value: 'services', label: t('onboarding.briefing.industries.services', 'Services') },
                            { value: 'consulting', label: t('onboarding.briefing.industries.consulting', 'Consulting') },
                            { value: 'marketing', label: t('onboarding.briefing.industries.marketing', 'Marketing') },
                            { value: 'retail', label: t('onboarding.briefing.industries.retail', 'Retail') },
                            { value: 'manufacturing', label: t('onboarding.briefing.industries.manufacturing', 'Manufacturing') },
                            { value: 'other', label: t('onboarding.briefing.industries.other', 'Other') }
                          ].map((ind) => (
                            <option key={ind.value} value={ind.value}>{ind.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Section 3: Brand & Audience */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Megaphone className="w-4 h-4 text-primary-500" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        {t('settings.sectionBrandAudience', 'Brand & Audience')}
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('settings.brandVoice', 'Brand Voice')}
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {[
                            { value: 'professional', label: t('onboarding.briefing.voices.professional', 'Professional') },
                            { value: 'casual', label: t('onboarding.briefing.voices.casual', 'Casual') },
                            { value: 'friendly', label: t('onboarding.briefing.voices.friendly', 'Friendly') },
                            { value: 'formal', label: t('onboarding.briefing.voices.formal', 'Formal') },
                            { value: 'bold', label: t('onboarding.briefing.voices.bold', 'Bold') },
                            { value: 'inspirational', label: t('onboarding.briefing.voices.inspirational', 'Inspirational') },
                            { value: 'educational', label: t('onboarding.briefing.voices.educational', 'Educational') }
                          ].map((voice) => (
                            <button
                              key={voice.value}
                              type="button"
                              onClick={() => setEditProfileData({ ...editProfileData, brand_voice: voice.value })}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                editProfileData.brand_voice === voice.value
                                  ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                                  : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                              }`}
                            >
                              {voice.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('settings.targetAudience', 'Target Audience')}
                        </label>
                        <textarea
                          value={typeof editProfileData.target_audience === 'object' ? (editProfileData.target_audience?.description || '') : editProfileData.target_audience}
                          onChange={(e) => setEditProfileData({
                            ...editProfileData,
                            target_audience: {
                              ...(typeof editProfileData.target_audience === 'object' ? editProfileData.target_audience : {}),
                              description: e.target.value
                            }
                          })}
                          placeholder={t('settings.targetAudiencePlaceholder', 'Describe your target audience...')}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Section 4: Marketing Goals */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-primary-500" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        {t('settings.sectionGoals', 'Marketing Goals')}
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { id: 'sales', icon: ShoppingCart, label: t('onboarding.goals.sales', 'More Sales'), color: 'from-green-500 to-emerald-500' },
                        { id: 'leads', icon: Users, label: t('onboarding.goals.leads', 'More Leads'), color: 'from-blue-500 to-cyan-500' },
                        { id: 'followers', icon: TrendingUp, label: t('onboarding.goals.followers', 'More Followers'), color: 'from-purple-500 to-pink-500' },
                        { id: 'awareness', icon: Eye, label: t('onboarding.goals.awareness', 'Brand Awareness'), color: 'from-orange-500 to-amber-500' },
                        { id: 'traffic', icon: MousePointerClick, label: t('onboarding.goals.traffic', 'More Traffic'), color: 'from-red-500 to-rose-500' },
                        { id: 'engagement', icon: Megaphone, label: t('onboarding.goals.engagement', 'More Engagement'), color: 'from-indigo-500 to-violet-500' }
                      ].map((goal) => {
                        const GoalIcon = goal.icon;
                        const isSelected = (editProfileData.marketing_goals || []).includes(goal.id);
                        return (
                          <button
                            key={goal.id}
                            type="button"
                            onClick={() => {
                              const current = editProfileData.marketing_goals || [];
                              const updated = isSelected
                                ? current.filter((g: string) => g !== goal.id)
                                : [...current, goal.id];
                              setEditProfileData({ ...editProfileData, marketing_goals: updated });
                            }}
                            className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute top-1.5 right-1.5">
                                <Check className="w-3.5 h-3.5 text-primary-500" />
                              </div>
                            )}
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${goal.color} flex items-center justify-center`}>
                              <GoalIcon className="w-4 h-4 text-white" />
                            </div>
                            <span className={`text-xs font-medium ${
                              isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {goal.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              <Button
                variant="ghost"
                onClick={() => setEditingProject(null)}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveProject}
                isLoading={savingProject}
                disabled={loadingProfile}
              >
                {t('common.save', 'Save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Page Selection Modal */}
      {showPageSelectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('settings.pageSelection.title')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {pageSelectionPlatform === 'linkedin'
                    ? t('settings.pageSelection.subtitleLinkedin')
                    : pageSelectionPlatform === 'facebook_ads' || pageSelectionPlatform === 'google_ads'
                    ? t('settings.pageSelection.subtitleAds')
                    : t('settings.pageSelection.subtitle')}
                </p>
              </div>
              <button
                onClick={handleClosePageSelection}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {loadingPages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : availablePages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>{t('settings.pageSelection.noPages')}</p>
                  <p className="text-sm mt-2">
                    {pageSelectionPlatform === 'instagram'
                      ? t('settings.pageSelection.noPagesInstagram')
                      : pageSelectionPlatform === 'facebook'
                      ? t('settings.pageSelection.noPagesFacebook')
                      : pageSelectionPlatform === 'facebook_ads' || pageSelectionPlatform === 'google_ads'
                      ? t('settings.pageSelection.noPagesAds')
                      : t('settings.pageSelection.noPagesGeneric')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availablePages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedPageId(page.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        selectedPageId === page.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      {page.picture ? (
                        <img
                          src={page.picture}
                          alt={page.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xl">
                          {page.type === 'personal' ? '👤' :
                           page.type === 'company' ? '🏢' :
                           page.type === 'ad_account' ? '📊' : '📄'}
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {page.name}
                        </p>
                        {page.username && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            @{page.username}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {page.type && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                              {page.type === 'personal' ? t('settings.pageSelection.personal') :
                               page.type === 'company' ? t('settings.pageSelection.company') :
                               page.type === 'page' ? t('settings.pageSelection.page') :
                               page.type === 'instagram_business' ? t('settings.pageSelection.business') :
                               page.type === 'ad_account' ? t('settings.pageSelection.adAccount') :
                               page.type}
                            </span>
                          )}
                          {page.category && (
                            <span>{page.category}</span>
                          )}
                          {page.currency && (
                            <span>{page.currency}</span>
                          )}
                          {(page.followers || page.subscribers) && (
                            <span>
                              {formatNumber(page.followers || page.subscribers)} {page.subscribers ? t('settings.pageSelection.subscribers') : t('settings.pageSelection.followers')}
                            </span>
                          )}
                        </div>
                        {page.facebook_page_name && (
                          <p className="text-xs text-gray-400 mt-1">
                            {t('settings.pageSelection.viaPage')}: {page.facebook_page_name}
                          </p>
                        )}
                        {page.business_name && (
                          <p className="text-xs text-gray-400 mt-1">
                            {page.business_name}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {selectedPageId === page.id ? (
                          <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={handleClosePageSelection}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSelectPage}
                disabled={!selectedPageId || selectingPage}
                isLoading={selectingPage}
              >
                {t('settings.pageSelection.confirmSelection')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FB Page Selection Modal (for facebook_ads) */}
      {showFbPageSelectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('settings.pageSelection.selectFbPageTitle')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.pageSelection.selectFbPageSubtitle')}
                </p>
              </div>
              <button
                onClick={handleCloseFbPageSelection}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {loadingFbPages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : availableFbPages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>{t('settings.pageSelection.noFbPagesFound')}</p>
                  <p className="text-sm mt-2">
                    {t('settings.pageSelection.noFbPagesHint')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableFbPages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedFbPageId(page.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        selectedFbPageId === page.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      {page.picture ? (
                        <img
                          src={page.picture}
                          alt={page.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xl">
                          📄
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {page.name}
                        </p>
                        {page.category && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {page.category}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {selectedFbPageId === page.id ? (
                          <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={handleCloseFbPageSelection}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSelectFbPage}
                disabled={!selectedFbPageId || selectingFbPage}
                isLoading={selectingFbPage}
              >
                {t('settings.pageSelection.confirmSelection')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ChatGPT Ads (OpenAI) API key modal */}
      {showOpenaiKeyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('settings.openaiAds.modalTitle', 'Conectar ChatGPT Ads')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.openaiAds.modalSubtitle', 'Cole a API key da sua conta de anúncios da OpenAI')}
                </p>
              </div>
              <button
                onClick={handleCloseOpenaiKeyModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <ol className="text-sm text-gray-600 dark:text-gray-300 list-decimal ml-5 space-y-1">
                <li>
                  {t('settings.openaiAds.step1', 'Acesse o Ads Manager da OpenAI em')}{' '}
                  <a href="https://ads.openai.com" target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">ads.openai.com</a>
                </li>
                <li>{t('settings.openaiAds.step2', 'Em Settings, gere uma API key da sua conta de anúncios')}</li>
                <li>{t('settings.openaiAds.step3', 'Cole a key abaixo — validamos e conectamos na hora')}</li>
              </ol>
              <Input
                type="password"
                value={openaiKeyInput}
                onChange={(e) => setOpenaiKeyInput(e.target.value)}
                placeholder={t('settings.openaiAds.keyPlaceholder', 'API key da OpenAI Ads')}
                autoFocus
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('settings.openaiAds.keySafety', 'A key fica armazenada com segurança e é usada apenas para sincronizar suas campanhas e métricas.')}
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="ghost" onClick={handleCloseOpenaiKeyModal}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveOpenaiKey}
                disabled={!openaiKeyInput.trim() || openaiKeySaving}
                isLoading={openaiKeySaving}
              >
                {t('settings.connect')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
